import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchChats,
  fetchCurrentUser,
  fetchPresets,
  login as loginRequest,
  logout as logoutRequest,
} from '../../utils/axios/api';
import {
  clearAuthCookies,
  getJwtFromCookie,
  getRefreshTokenFromCookie,
  setAuthCookies,
} from '../../utils/cookies';
import { extractExceptionCode } from '../../utils/exception';
import { User } from '../types';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: User | null;
  token: string | null;
  status: AuthStatus;
}

const initialState: AuthState = {
  user: null,
  token: null,
  status: 'idle',
};

async function loadAuthenticatedSession() {
  const [user, chats, presets] = await Promise.all([
    fetchCurrentUser(),
    fetchChats(),
    fetchPresets(),
  ]);

  return {
    user,
    token: getJwtFromCookie() ?? null,
    chats,
    presets,
  };
}

export const initAuth = createAsyncThunk(
  'auth/init',
  async (_, { rejectWithValue }) => {
    if (!getJwtFromCookie() && !getRefreshTokenFromCookie()) {
      return rejectWithValue('NO_TOKEN');
    }

    try {
      return await loadAuthenticatedSession();
    } catch {
      clearAuthCookies();
      return rejectWithValue('UNAUTHORIZED');
    }
  },
);

export const login = createAsyncThunk(
  'auth/login',
  async (token: string, { rejectWithValue }) => {
    try {
      const { accessToken, refreshToken } = await loginRequest(token);
      setAuthCookies(accessToken, refreshToken);
      return await loadAuthenticatedSession();
    } catch (error) {
      return rejectWithValue(extractExceptionCode(error) ?? 'LOGIN_FAILED');
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await logoutRequest();
  } catch {
    // Clear local session even if the API call fails
  }

  clearAuthCookies();
});

export const refreshCurrentUser = createAsyncThunk(
  'auth/refreshCurrentUser',
  fetchCurrentUser,
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuth: () => ({
      user: null,
      token: null,
      status: 'unauthenticated' as const,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(initAuth.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(initAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.status = 'authenticated';
      })
      .addCase(initAuth.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.status = 'unauthenticated';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.status = 'authenticated';
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.status = 'unauthenticated';
      })
      .addCase(refreshCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  },
});

export const { resetAuth } = authSlice.actions;
export default authSlice.reducer;
